import { Inject, Injectable } from '@angular/core';
import gql from 'graphql-tag';
import { BehaviorSubject, Observable, Subject, combineLatest, of } from 'rxjs';
import { first, map, shareReplay, startWith, switchMap } from 'rxjs/operators';

import { getUpdatedSystems } from './systems.utils';
import { StateService } from '../state/state.service';
import { System, Systems } from '../models';
import { ConnextData, MyCertifiedPlant } from '../telemetry';
import { GetConnextDataQuery } from '../telemetry/index.graph-ql';
import { fromAwsSystemListToSystemList } from './aws.systems.utils';
import { CORE_SERVICES_CONFIG, CoreServiceConfig } from '../../core-services.config';

@Injectable({
  providedIn: 'root',
})
export class SystemsService {
  private isUpdateSystemRequestPending = false;
  private systemsTypes: ('aws' | 'firebase')[] = [];
  /**
   * This refresh is used only when environment is changed. (es. prod -> dev)
   * or temporary when the connext config is changed.
   */
  readonly refresh$ = new Subject<void>();
  readonly _awsSystems$ = new BehaviorSubject<Array<Partial<MyCertifiedPlant>> | undefined>(undefined);
  readonly awsSystems$: Observable<Systems | undefined> = combineLatest([
    this._awsSystems$,
    this.refresh$.pipe(startWith(undefined)),
  ]).pipe(
    map(([_awsSystems]) => _awsSystems ? fromAwsSystemListToSystemList(_awsSystems) : _awsSystems),
    shareReplay({ bufferSize: 1, refCount: true }),
  );
  readonly _firebaseSystems$ = new BehaviorSubject<Systems | undefined>(undefined);
  readonly firebaseSystems$: Observable<Systems | undefined> = combineLatest([
    this._firebaseSystems$,
    this.refresh$.pipe(
      startWith(undefined),
    ),
  ]).pipe(
    map(([systems]) => systems),
    switchMap(async (systems?: Systems) => {
      /**
       * @Important check if all system has system id (unique)
       * else generate(on save function) a new one and save it.
       * */
      const systemsWithoutIds = systems?.filter((s: System) => !s.uid);
      if (systemsWithoutIds?.length && !this.isUpdateSystemRequestPending) {
        this.isUpdateSystemRequestPending = true;
        /** Save the system with the new id */
        for (const system of systemsWithoutIds) {
          /** If the system is virtual and has no uid, generate a new one */
          await this.stateService.saveCurrentSystem(system);
        }
        return undefined;
      }
      // if the activeZone is not defined, set the first temporaryZoneList
      systems?.forEach((s: System) => {
        if( s.configuration.activeZone === undefined && s.configuration.temporaryZoneList.length > 0) {
          s.configuration.activeZone = s.configuration.temporaryZoneList[0];
        };
      });

      return systems;
    }),
    map((systems) => {
      const isVirtual = (s: System) => !s.configuration?.connextId;
      const withoutDevices = (s: System) => !s.devices?.length;
      /**
       * filter virtual systems without devices
       */
      return systems?.filter((s: System) => !(isVirtual(s) && withoutDevices(s)));
    }),
    map((systems): Systems | undefined => systems?.map((s: System) => ({
      ...s,
      source: 'firebase' as const,
    }))),
    shareReplay({ bufferSize: 1, refCount: true }),
  );


  readonly systems$: Observable<Systems | undefined>;

  constructor(
    @Inject(CORE_SERVICES_CONFIG) config: CoreServiceConfig,
    private readonly getConnextDataQuery: GetConnextDataQuery,
    private readonly stateService: StateService,
  ) {
    this.systemsTypes = config.systemsTypes;
    // Build two sources, push real stream if enabled, otherwise of([]) to simplify downstream
    const firebaseSource = this.systemsTypes.includes('firebase')
      ? this.firebaseSystems$
      : of([]);
    const awsSource = this.systemsTypes.includes('aws')
      ? this.awsSystems$
      : of([]);

    this.systems$ = combineLatest([firebaseSource, awsSource]).pipe(
      map(([firebaseSystems, awsSystems]) => {
        if (firebaseSystems && awsSystems) {
          return [...firebaseSystems, ...awsSystems];
        }
        return undefined;
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.stateService.data$.subscribe((data) => this._firebaseSystems$.next(
      /** Sort Systems by name */
      data?.systems ? data?.systems.sort((a: System, b: System) => a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1) : data?.systems,
    ));

    this.stateService.awsRawData$.subscribe((data) => {
      this._awsSystems$.next(
        data
        ? data.sort((a, b) => (a.plantPref?.plantName?.toLowerCase() ?? '') > (b.plantPref?.plantName?.toLowerCase() ?? '') ? 1 : -1)
        : undefined);
    });

    this.stateService.user$.subscribe((user) => this.refresh$.next());
  }


  async findSystem(uid: string): Promise<System> {
    const systems = await this.systems$.pipe(first()).toPromise();
    const system = systems?.find((s: System) => s.uid === uid);
    if (system) {
      return system;
    }
    throw new Error('System not found');
  }

  async getConnextDevices(id: string, refresh = true): Promise<Partial<ConnextData>> {
    const query = gql`
    fragment connextdata on ConnextData {
      id
      info {
        sn {
          sn
        }
      }
      devices {
        ...on EvChType {
          timestamp
          sn
          active
        }
        ...on inverterType {
          timestamp
          sn
          active
        }
      }
    }
  `;
    const results = await this.getConnextDataQuery.execute(query, { id }, refresh);
    return results.data.getConnextData;
  }

  /**
   * This method is used to update the systems with the connext data.
   */
  async updateConnextDeviceList(system: System): Promise<Partial<ConnextData>> {
    if (!system.configuration.connextId) {
      throw new Error('Connext ID is not defined');
    }
    const systems = await this._firebaseSystems$.pipe(first()).toPromise() ?? [];
    const connextData = await this.getConnextDevices(system.configuration.connextId);
    const updatedSystems = await getUpdatedSystems(system, connextData, systems);
    const updatedSystem = updatedSystems?.find((s) => s.uid === system.uid);
    if (updatedSystem) {
      await this.stateService.saveCurrentSystem(updatedSystem);
    }
    return connextData;
  }
}
